import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ticketsAPI } from '../../../services/api';
import i18n from '../../../i18n';

// Async thunks
export const fetchTicketStatus = createAsyncThunk(
  'tickets/fetchStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ticketsAPI.getStatus();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const claimTicket = createAsyncThunk(
  'tickets/claim',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ticketsAPI.claimTicket();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  isAvailable: false,
  nextAvailableTime: null,
  lastClaimTime: null,
  isAutoCollectEnabled: false,
  autoCollectEndTime: null,
  boostEnabled: false,
  boostTickets: 0,
  totalTicketsClaimed: 0,
  totalAutoCollected: 0,
  pendingTickets: 1,
  isLoading: false,
  error: null,
  isInitialized: false,
};

const ticketsSlice = createSlice({
  name: 'tickets',
  initialState,
  reducers: {
    updateFromWebSocket: (state, action) => {
      // ... existing code ...
    },
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setAutoCollectEnabled: (state, action) => {
      state.isAutoCollectEnabled = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch status
      .addCase(fetchTicketStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.isAvailable = action.payload.isAvailable;
        state.nextAvailableTime = action.payload.nextAvailableTime || null;
        state.lastClaimTime = action.payload.lastClaimTime || null;
        state.isAutoCollectEnabled = action.payload.isAutoCollectEnabled;
        state.autoCollectEndTime = action.payload.autoCollectEndTime || null;
        state.boostTickets = action.payload.boostTickets ?? 0;
        state.boostEnabled = (action.payload.boostTickets ?? 0) > 0;
        state.totalTicketsClaimed = action.payload.totalTicketsClaimed || 0;
        state.totalAutoCollected = action.payload.totalAutoCollected || 0;
        state.pendingTickets = action.payload.pendingTickets ?? 1;
      })
      .addCase(fetchTicketStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true; // Mark as initialized even on error to stop loop
        state.error = action.payload || i18n.t('common.unknown_error', 'Unknown error');
      })
      // Claim ticket
      .addCase(claimTicket.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(claimTicket.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAvailable = false;
        state.nextAvailableTime = action.payload.nextAvailableTime || null;
      })
      .addCase(claimTicket.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { updateFromWebSocket, clearError, setLoading, setAutoCollectEnabled } = ticketsSlice.actions;
export default ticketsSlice.reducer;

